import { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { FlexColumn } from 'ui-components/components/flex';
import { Gap } from 'ui-components/components/flex/gap';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { editTemplate, saveTemplate } from '../../../components/html-editor/html-template.api';
import { TemplateType } from '../../../components/html-editor/types';
import { useLLMActionBinding } from '../../../components/shippy-assistant/hooks/use-llm-action-binding';
import { PACKING_SLIP_UPDATE_ACTION_ID } from '../../../components/shippy-assistant/llm-action-registry/action-definitions';
import { PAGE_SLOT } from '../../../components/shippy-assistant/llm-action-registry/types';
import { useEnableNewHtmlEditorForPackingSlips } from '../../../feature-flags/hooks/use-enable-new-html-editor-for-packing-slips';
import { ButtonWithLockIcon } from '../../../feature-gates/button-with-lock-icon';
import { usePackingSlipsCustomizedFeatureGate } from '../../../feature-gates/use-packing-slips-customized-feature-gate';
import { useModal } from '../../../hooks/use-modal';
import Global from '../../../store/global-selectors';
import { refreshPackingSlipTemplates, refreshStoreList } from '../../../store/seller/seller.actions';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';
import sharedStyles from '../settings-page.module.less';
import { getPackingSlipTemplates, getTemplateHtmlContent } from './api';
import { CreateUpdatePackingSlipTemplate } from './components/create-update-template';
import { DeleteTemplate } from './components/delete-template';
import { GridLayout } from './components/grid-layout';
import { SelectStoreDropdown } from './components/select-store-dropdown/select-store-dropdown';
import { SIZE_LETTER } from './constants';
import messages from './messages';
import { apiError } from './on-api-error';
import styles from './styles.module.less';
import { HtmlEditorDrawer } from '../../../components/html-editor/html-editor-drawer';
import { OrderNumberSearch } from '../../../components/html-editor/source-id-search/order-number-search';

const findPackingSlipOrDefault = (idToUpdate, packingSlips) => {
  if (!idToUpdate) {
    return {};
  }
  return packingSlips.find((ps) => ps.packingSlipId === idToUpdate) || {};
};

export const PackingSlipTemplates = () => {
  const dispatch = useDispatch();
  const [packingSlips, setPackingSlips] = useAsyncSafeState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const stores = useSelector(Global.selectSellerStores);
  const { basicPackingSlipsCustomizedFeature: hasCustomizedPackingSlips } = usePackingSlipsCustomizedFeatureGate();
  const isNewHtmlEditorEnabled = useEnableNewHtmlEditorForPackingSlips();
  const { open: openHtmlEditor, close: closeHtmlEditor, isOpen: isHtmlEditorOpen } = useModal();
  const [shouldLaunchEditor, setShouldLaunchEditor] = useState(false);

  useLLMActionBinding(
    PACKING_SLIP_UPDATE_ACTION_ID,
    () => {
      setShouldLaunchEditor(true);
      return { success: true };
    },
    { slot: PAGE_SLOT },
  );

  useEffect(() => {
    setShouldRefresh(false);
    setIsLoadingTemplates(true);
    getPackingSlipTemplates()
      .then((response) => {
        let responsePackingSlips = response.data || [];
        if (!isNewHtmlEditorEnabled) {
          responsePackingSlips = responsePackingSlips.filter((template) => !template.htmlTemplateKey);
        }
        setPackingSlips(responsePackingSlips);
      })
      .catch(() => apiError(messages.apiFetchError))
      .finally(() => setIsLoadingTemplates(false));
  }, [setPackingSlips, shouldRefresh, isNewHtmlEditorEnabled]);

  const sharedLetterTemplate = useMemo(
    () => packingSlips.find((t) => t.htmlTemplateKey && t.shared && t.size === SIZE_LETTER),
    [packingSlips],
  );

  const {
    data: sharedLetterTemplateHtml,
    isLoading: isLoadingSharedLetterTemplateHtml,
    isError: isSharedLetterTemplateHtmlError,
  } = useQuery({
    queryKey: ['packing-slip-html', sharedLetterTemplate?.packingSlipId],
    queryFn: () => getTemplateHtmlContent(sharedLetterTemplate?.packingSlipId),
    enabled: !!sharedLetterTemplate && isNewHtmlEditorEnabled,
    staleTime: Infinity,
  });
  const [isLoadingHtmlContent, setIsLoadingHtmlContent] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [isCopyHtml, setIsCopyHtml] = useState(false);
  const [idToUpdate, setIdToUpdate] = useState();
  const [templateToEdit, setTemplateToEdit] = useState(null);
  const [storeGuids, setStoreGuids] = useState(undefined);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const onCreateNewTemplate = useCallback(() => {
    setIsCopy(false);
    setShouldLaunchEditor(true);
  }, []);

  const onCreateNewHtmlTemplate = useCallback(() => {
    if (sharedLetterTemplateHtml) {
      setTemplateToEdit({
        body: sharedLetterTemplateHtml.html,
        layout: sharedLetterTemplateHtml.layout,
        orientation: sharedLetterTemplateHtml.orientation,
      });
    } else if (isSharedLetterTemplateHtmlError) {
      apiError(messages.apiFetchError);
    }
    openHtmlEditor();
  }, [sharedLetterTemplateHtml, isSharedLetterTemplateHtmlError, openHtmlEditor]);

  const onEditPackingSlip = useCallback(
    async (template) => {
      if (!template.htmlTemplateKey) {
        setIdToUpdate(template.packingSlipId);
        setIsCopy(false);
        setShouldLaunchEditor(true);
        return;
      }
      if (isNewHtmlEditorEnabled) {
        setTemplateToEdit({
          packingSlipId: template.packingSlipId,
          htmlTemplateKey: template.htmlTemplateKey,
          name: template.name,
        });
        setIsCopyHtml(false);
        setIsLoadingHtmlContent(true);
        openHtmlEditor();
        try {
          const data = await getTemplateHtmlContent(template.packingSlipId);
          setTemplateToEdit((prev) => ({
            ...prev,
            body: data.html,
            layout: data.layout,
            orientation: data.orientation,
          }));
        } catch {
          closeHtmlEditor();
          setTemplateToEdit(null);
          apiError(messages.apiFetchError);
        } finally {
          setIsLoadingHtmlContent(false);
        }
      }
    },
    [openHtmlEditor, closeHtmlEditor, isNewHtmlEditorEnabled],
  );

  const onCopyPackingSlip = useCallback(
    async (template) => {
      if (!template.htmlTemplateKey) {
        setIdToUpdate(template.packingSlipId);
        setIsCopy(true);
        setShouldLaunchEditor(true);
        return;
      }
      if (isNewHtmlEditorEnabled) {
        setTemplateToEdit({ name: template.name });
        setIsCopyHtml(true);
        setIsLoadingHtmlContent(true);
        openHtmlEditor();
        try {
          const data = await getTemplateHtmlContent(template.packingSlipId);
          setTemplateToEdit((prev) => ({
            ...prev,
            body: data.html,
            layout: data.layout,
            orientation: data.orientation,
          }));
        } catch {
          closeHtmlEditor();
          setTemplateToEdit(null);
          setIsCopyHtml(false);
          apiError(messages.apiFetchError);
        } finally {
          setIsLoadingHtmlContent(false);
        }
      }
    },
    [openHtmlEditor, closeHtmlEditor, isNewHtmlEditorEnabled],
  );

  const onCloseEditor = useCallback(() => {
    setShouldLaunchEditor(false);
    setIdToUpdate(undefined);
  }, []);

  const onCloseHtmlEditor = useCallback(() => {
    closeHtmlEditor();
    setTemplateToEdit(null);
    setIsCopyHtml(false);
    setStoreGuids(undefined);
    setIsLoadingHtmlContent(false);
    setSelectedOrder(null);
  }, [closeHtmlEditor]);

  const onHtmlTemplateSaved = useCallback(() => {
    setShouldRefresh(true);
    dispatch(refreshStoreList());
    dispatch(refreshPackingSlipTemplates());
    setTemplateToEdit(null);
    setIsCopyHtml(false);
  }, [dispatch]);

  const handleSaveHtmlTemplate = useCallback(
    (data) => {
      const request = {
        name: data.name,
        html: data.html,
        layout: data.layout,
        orientation: data.orientation,
      };

      if (storeGuids !== undefined) {
        request.settings = { storeGuids };
      }

      if (!isCopyHtml && templateToEdit?.packingSlipId) {
        return editTemplate(TemplateType.PackingSlip, templateToEdit.packingSlipId, request);
      }
      return saveTemplate(TemplateType.PackingSlip, request);
    },
    [storeGuids, isCopyHtml, templateToEdit],
  );

  const onTemplateSaved = useCallback(() => {
    setShouldRefresh(true);
    setIdToUpdate(undefined);
  }, []);

  const [templateToConfirmDeletion, setTemplateToConfirmDeletion] = useState();
  const onDeletePackingSlip = useCallback((template) => {
    setTemplateToConfirmDeletion(template);
  }, []);
  const onConfirmDeletePackingSlip = () => {
    setTemplateToConfirmDeletion(undefined);
  };
  const onDeletedPackingSlip = useCallback(() => {
    setTemplateToConfirmDeletion(undefined);
    setShouldRefresh(true);
  }, []);

  const onCancelDeletePackingSlip = useCallback(() => {
    setTemplateToConfirmDeletion(undefined);
  }, []);

  const selectedTemplate = findPackingSlipOrDefault(idToUpdate, packingSlips);

  const additionalFormFields = (
    <FlexColumn className={styles['additional-form-field']} gap={Gap.Space4}>
      <SelectStoreDropdown onSelectionChange={setStoreGuids} packingSlipId={isCopyHtml ? undefined : templateToEdit?.packingSlipId} />
    </FlexColumn>
  );

  const sourceIds = selectedOrder?.sourceIds ?? null;

  const orderSearchToolbar = useMemo(
    () => (
      <OrderNumberSearch
        isDisabled={false}
        onClear={() => setSelectedOrder(null)}
        onOrderSelected={setSelectedOrder}
        selectedOrder={selectedOrder}
      />
    ),
    [selectedOrder, setSelectedOrder],
  );

  return (
    <div className={classnames(sharedStyles.content, styles['template-content'])}>
      <SettingsPageTitle value={messages.header} />
      <Text className={styles.summary} textType={TextType.Description} value={messages.pageSummary} />
      <div>
        <DeleteTemplate
          onCancel={onCancelDeletePackingSlip}
          onConfirm={onConfirmDeletePackingSlip}
          onDeleted={onDeletedPackingSlip}
          packingSlip={templateToConfirmDeletion}
        />
        {shouldLaunchEditor ? (
          <CreateUpdatePackingSlipTemplate
            isCopy={isCopy}
            onClose={onCloseEditor}
            onSaved={onTemplateSaved}
            packingSlip={selectedTemplate}
          />
        ) : null}
        <div className={styles['grid-header-row']}>
          <Text textType={TextType.H3} value={messages.tableHeaderPackingSlips} />
          <ButtonWithLockIcon
            disabled={!hasCustomizedPackingSlips || isLoadingTemplates || isLoadingSharedLetterTemplateHtml}
            isLoading={isLoadingTemplates || isLoadingSharedLetterTemplateHtml}
            onClick={isNewHtmlEditorEnabled ? onCreateNewHtmlTemplate : onCreateNewTemplate}
          >
            <FormattedMessage {...messages.createNewTemplate} />
          </ButtonWithLockIcon>
        </div>
        <GridLayout
          items={packingSlips}
          onCopyTemplate={onCopyPackingSlip}
          onDeleteTemplate={onDeletePackingSlip}
          onEditTemplate={onEditPackingSlip}
          stores={stores}
        />
        {isHtmlEditorOpen ? (
          <HtmlEditorDrawer
            additionalFormFields={additionalFormFields}
            initialLayout={templateToEdit?.layout}
            initialOrientation={templateToEdit?.orientation}
            initialTemplateName={templateToEdit?.name ?? ''}
            initialValue={templateToEdit?.body ?? ''}
            isLoadingContent={isLoadingHtmlContent}
            onCancel={onCloseHtmlEditor}
            onSave={handleSaveHtmlTemplate}
            onSuccessfulSave={onHtmlTemplateSaved}
            previewToolbar={orderSearchToolbar}
            sourceIds={sourceIds}
            templateKey={templateToEdit?.htmlTemplateKey}
            templateType={TemplateType.PackingSlip}
            title={<Text textType={TextType.H3} value={messages.htmlEditorTitle} />}
          />
        ) : null}
      </div>
    </div>
  );
};
